import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, Cell, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Activity, Inbox, FileText, Phone, MessageSquare, Calendar, ChevronRight, Globe, Users, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, KpiTile, SectionLabel, fmt, AnalyticsOffline } from "./ui";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { TrackingConnect } from "./TrackingConnect";

const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
  </svg>
);

export default function CPerformance() {
  const { accent, clientId, base } = useCockpit();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const load = () => api.get(`/cockpit/analytics?lang=${lang}${clientId ? `&client_id=${clientId}` : ""}`).then((r) => setA(r.data));
  useEffect(() => { load(); }, [clientId, lang]);
  if (!a) return <Spinner />;

  const today = new Date();
  const series = (a.visits_30d || []).slice(-30);
  const chart = series.map((v, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (series.length - 1 - i));
    return { v, label: d.toLocaleDateString("de-CH", { day: "numeric", month: "short" }) };
  });
  const max = Math.max(1, ...(series.length ? series : [1]));

  // ECHT-Regel: Top-Seiten & Telefon-Klicks brauchen verbundene Analyse (GA4/KEWAGPage Light).
  // Ohne Verbindung KEINE erfundenen Zahlen — ehrlicher Leer-Zustand. Anfragen & Formular-Nachrichten
  // kommen immer echt aus der Datenbank.
  const live = !!a.live;
  const phoneVal = live ? (a.phone_clicks ?? 0) : null;      // null => ehrlicher Leer-Zustand
  const formVal = live ? (a.form_messages ?? 0) : (a.form_messages_real ?? 0);
  const topPages = live ? (a.top_pages || []) : [];

  const hl = a.monthly_highlight;
  const hlColor = hl?.tone === "negative" ? "#F59E0B" : hl?.tone === "neutral" ? "#9CA3AF" : accent;
  const HlIcon = hl?.tone === "negative" ? TrendingDown : hl?.tone === "neutral" ? Activity : TrendingUp;

  const sources = [
    { key: "google", name: "Google", pct: a.sources?.google || 0, icon: <GoogleG className="w-5 h-5" />, note: t("«Google» = über die Google-Suche gefunden.") },
    { key: "direct", name: t("Direkt"), pct: a.sources?.direct || 0, icon: <Globe className="w-5 h-5 text-gray-300" />, note: t("«Direkt» = Adresse direkt eingegeben.") },
    { key: "social", name: t("Social Media"), pct: a.sources?.social || 0, icon: <Users className="w-5 h-5" style={{ color: "#8B5CF6" }} />, note: t("«Social Media» = über soziale Netzwerke.") },
  ];

  return (
    <div className="fade-in space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}><TrendingUp className="w-4 h-4" /> {t("Statistik")}</p>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight mt-1">{t("Besuche & Performance")}</h1>
          <p className="text-sm text-gray-400 mt-1.5">{t("So entwickelt sich Ihre Website — und was die Zahlen bedeuten.")}</p>
        </div>
        {a.live ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border border-[#A3E635]/40 bg-[#A3E635]/10 text-[#A3E635] shrink-0" data-testid="perf-live-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A3E635]" /> {a.live_label ? t(a.live_label) : t("Live-Daten")}
          </span>
        ) : (
          <div className="flex items-center gap-2.5 shrink-0">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border border-white/15 bg-white/5 text-gray-400" data-testid="perf-notconnected-badge">
              <WifiOff className="w-3 h-3" /> {t("Analyse nicht verbunden")}
            </span>
            <TrackingConnect clientId={clientId} accent={accent} onConnected={load} />
          </div>
        )}
      </div>

      {a.live && (
        <p className="text-xs text-gray-500 -mt-2" data-testid="perf-live-note">
          {t("Diese Zahlen stammen live von Ihrer Website (KEWAGPage Analytics Light, datenschutzfreundlich — keine IP-Speicherung, keine Profile).")}
        </p>
      )}

      {!a.live && (
        <p className="text-xs text-gray-500 -mt-2" data-testid="perf-notconnected-note">
          {t("Hinweis: Besuche, Verlauf, Quellen, Top-Seiten und Telefon-Klicks erscheinen mit echten Zahlen, sobald Ihre Website-Analyse verbunden ist. Anfragen und Formular-Nachrichten sind echt aus Ihrer Datenbank.")}
        </p>
      )}

      {hl && (
        <Card className="p-5 flex items-start gap-4" data-testid="perf-monthly-highlight" style={{ borderColor: `${hlColor}55`, background: `${hlColor}0D` }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${hlColor}1F` }}>
            <HlIcon className="w-6 h-6" style={{ color: hlColor }} />
          </div>
          <div className="min-w-0">
            <div className="font-black text-base sm:text-lg tracking-tight" style={{ color: hlColor }} data-testid="perf-highlight-headline">{hl.headline}</div>
            <p className="text-sm text-gray-300 mt-1" data-testid="perf-highlight-text">{hl.text}</p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <KpiTile icon={TrendingUp} tone="lime" accent={accent} value={live ? fmt(a.total_visits) : "—"} label={t("Besuche")} sub={live ? t("vs. Vormonat") : t("sobald Analyse verbunden")} trend={live ? `${(a.delta_pct ?? 0) >= 0 ? "+" : ""}${a.delta_pct ?? 0}%` : null} testid="perf-visits" />
        <KpiTile icon={Inbox} tone="blue" accent={accent} value={a.total_leads} label={t("Anfragen")} sub={t("über die Website")} testid="perf-leads" />
        <KpiTile icon={Phone} tone="green" accent={accent} value={phoneVal == null ? "—" : phoneVal} label={t("Telefon-Klicks")} sub={phoneVal == null ? t("sobald Analyse verbunden") : t("Direktkontakte")} testid="perf-phone" />
        <KpiTile icon={MessageSquare} tone="purple" accent={accent} value={formVal} label={t("Formular-Nachrichten")} sub={t("über Kontaktformular")} testid="perf-forms" />
      </div>

      <Card className="p-5" data-testid="perf-visits-chart">
        <div className="flex items-center justify-between gap-3 mb-3">
          <SectionLabel>{t("Besuche der letzten 30 Tage")}</SectionLabel>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-300 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 shrink-0" data-testid="perf-range-select">
            <Calendar className="w-3.5 h-3.5" style={{ color: accent }} /> {t("Letzte 30 Tage")}
          </div>
        </div>
        {live ? (
          <>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={chart} barCategoryGap={2} margin={{ top: 4, right: 4, left: -4, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" interval={3} tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} dy={4} />
                <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#121217", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#fff", fontSize: 12 }} labelFormatter={(l) => l} formatter={(v) => [v, t("Besuche")]} />
                <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                  {chart.map((e, i) => <Cell key={i} fill={accent} fillOpacity={0.35 + 0.65 * (e.v / max)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 mt-2">{t("Jede Säule steht für einen Tag. Mehr Besuche bedeuten mehr potenzielle Kundenkontakte.")}</p>
          </>
        ) : (
          <AnalyticsOffline t={t} minH={210} testid="perf-chart-offline" action={<TrackingConnect clientId={clientId} accent={accent} onConnected={load} />} />
        )}
      </Card>

      {live ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
          {sources.map((s) => (
            <Card key={s.key} className="p-5" data-testid={`perf-source-${s.key}`}>
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-white/5 border border-white/10">{s.icon}</span>
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">{s.name}</span>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-[30px] font-black tracking-tight leading-none" style={{ color: accent }}>{s.pct}%</span>
                <span className="text-xs text-gray-500">{t("Besuche")}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 mt-3 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: accent }} /></div>
              <p className="text-[11px] text-gray-500 mt-3">{s.note}</p>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-5" data-testid="perf-sources-offline">
          <SectionLabel>{t("Woher die Besucher kommen")}</SectionLabel>
          <AnalyticsOffline t={t} minH={120} />
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5 flex flex-col" data-testid="perf-top-pages">
          <SectionLabel>{t("Top-Seiten")}</SectionLabel>
          {topPages.length ? (
            <>
              <div className="flex-1">
                {topPages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                    <span className="text-sm text-gray-300 flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /> {p.page}</span>
                    <span className="font-bold text-sm">{fmt(p.views)}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => nav(`${base}/website`)} data-testid="perf-all-pages-btn"
                className="mt-3 self-start flex items-center gap-1 text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: accent }}>
                {t("Alle Seiten ansehen")} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-10 px-4" data-testid="perf-top-pages-empty">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 mb-3">
                <FileText className="w-5 h-5 text-gray-500" />
              </div>
              <div className="text-sm font-semibold text-gray-300">{t("Noch keine Echtdaten")}</div>
              <p className="text-xs text-gray-500 mt-1 max-w-[280px]">{t("Sobald Ihre Website-Analyse verbunden ist, erscheinen hier Ihre echten Top-Seiten.")}</p>
            </div>
          )}
        </Card>
        <Card className="p-5 flex flex-col" data-testid="perf-results">
          <SectionLabel>{t("Ergebnisse")}</SectionLabel>
          <div className="flex-1">
            <Result icon={Inbox} value={a.total_leads} label={t("Anfragen")} accent={accent} />
            {phoneVal == null ? (
              <div className="flex items-center gap-3 py-2.5 border-b border-white/5" data-testid="perf-phone-empty">
                <Phone className="w-5 h-5 shrink-0 text-gray-600" />
                <span className="text-sm text-gray-500 flex-1">{t("Klicks auf Telefonnummer")}</span>
                <span className="text-[11px] text-gray-600">{t("sobald Analyse verbunden")}</span>
              </div>
            ) : (
              <Result icon={Phone} value={phoneVal} label={t("Klicks auf Telefonnummer")} accent={accent} />
            )}
            <Result icon={MessageSquare} value={formVal} label={t("Kontaktformular-Nachrichten")} accent={accent} />
          </div>
          <button onClick={() => nav(`${base}/leads`)} data-testid="perf-all-results-btn"
            className="mt-3 self-start flex items-center gap-1 text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: accent }}>
            {t("Alle Ergebnisse ansehen")} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </Card>
      </div>

      {/* Empfehlung */}
      <Card className="p-5" style={{ borderColor: `${accent}33` }} data-testid="perf-recommendation">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A` }}>
            <TrendingUp className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm">{t("Unsere Empfehlung")}</div>
            <p className="text-sm text-gray-300 mt-0.5">{a.recommendation}</p>
            <button onClick={() => nav(`${base}/next-steps`)} data-testid="perf-recommendations-btn"
              className="mt-3 flex items-center gap-1 text-xs font-semibold hover:opacity-80 transition-opacity" style={{ color: accent }}>
              {t("Empfehlungen ansehen")} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

const Result = ({ icon: Icon, value, label, accent }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
    <Icon className="w-5 h-5 shrink-0" style={{ color: accent }} />
    <span className="text-xl font-black tracking-tight w-8">{value}</span>
    <span className="text-sm text-gray-400">{label}</span>
  </div>
);
